import React, { useEffect } from "react";
import { useState, useMemo } from "react"
import { FaSearch, FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaTimes, FaCircle, FaClock } from "react-icons/fa"
import "./event_calender.css"
import { showToast, ToastMessage } from "../Shared/ToastMessage"
import axios from "axios"
import { localserver } from "../../api/common"
import Header from "./Header"
import Footer from "./Footer"

export default function EventsPage() {
  const [activeTab, setActiveTab] = useState("upcoming")
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedEvents, setSelectedEvents] = useState([])
  const [categories, setCategories] = useState({
    upcoming: [],
    past: [],
    zonal: [],
    mmas: [],
    fmas: [],
    slp: [],
  })
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [zones, setZones] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedZone, setSelectedZone] = useState("all")
  const [getEventStatus, setGetEventStatus] = useState(false)

  const today = new Date()
  const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return {
      day: date.getDate(),
      month: date.toLocaleDateString("en-US", { month: "short" }),
      fullDate: date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
      fullDateTime: date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    }
  }

  const formatTime = (timeString) => {
    if (!timeString) return ""
    const time = new Date(`1970-01-01T${timeString}`)
    return time.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  }

  const upcomingKeys = useMemo(() => {
    return new Set(upcomingEvents.map((e) => (e.id ? e.id : `${e.event_name}-${e.start_date}`)))
  }, [upcomingEvents])

  const allEvents = useMemo(() => {
    const eventMap = new Map()
    Object.values(categories).forEach((catEvents) => {
      catEvents.forEach((event) => {
        const key = event.id ? event.id : `${event.event_name}-${event.start_date}`
        if (!eventMap.has(key)) {
          eventMap.set(key, event)
        }
      })
    })
    return Array.from(eventMap.values())
  }, [categories])

  const eventsToShow = useMemo(() => {
    let events = categories[activeTab] || []
    if (activeTab === "zonal" && selectedZone !== "all") {
      events = events.filter((e) => e.zone === selectedZone)
    }
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.toLowerCase()
      events = events.filter(
        (event) =>
          (event.event_name || "").toLowerCase().includes(searchTerm) ||
          (event.venue_city || "").toLowerCase().includes(searchTerm) ||
          (event.venue_state || "").toLowerCase().includes(searchTerm) ||
          (event.description || "").toLowerCase().includes(searchTerm) ||
          (event.event_type || "").toLowerCase().includes(searchTerm) ||
          (event.tags || "").toLowerCase().includes(searchTerm),
      )
    }
    return events
  }, [categories, activeTab, searchQuery, selectedZone])

  const handleSearch = (value) => {
    setSearchQuery(value)
  }

  const getEventsForDate = (date) => {
    return allEvents.filter((event) => {
      const eventDate = new Date(event.start_date)
      const eventDateString = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(eventDate.getDate()).padStart(2, "0")}`
      return eventDateString === date
    })
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
      const hasEvents = allEvents.some((event) => {
        const eventDate = new Date(event.start_date)
        const eventDateString = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(eventDate.getDate()).padStart(2, "0")}`
        return eventDateString === dateString
      })
      days.push({ day, dateString, hasEvents })
    }

    return days
  }

  const handleDateClick = (dateString) => {
    const eventsForDate = getEventsForDate(dateString)
    if (eventsForDate.length > 0) {
      setSelectedDate(dateString)
      setSelectedEvents(eventsForDate)
    } else {
      setSelectedDate(null)
      setSelectedEvents([])
    }
  }

  const clearSelection = () => {
    setSelectedDate(null)
    setSelectedEvents([])
  }

  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      if (direction === "prev") {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
    setSelectedDate(null)
    setSelectedEvents([])
  }

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href)
  }

  const monthYear = currentDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  })
  const days = getDaysInMonth(currentDate)
  const weekDays = ["S", "M", "T", "W", "T", "F", "S"]

  const handleRegister = (registrationLink) => {
    window.open(registrationLink, "_blank", "noopener,noreferrer")
  }

  const isUpcomingFromApi = (event) => {
    const eventKey = event.id ? event.id : `${event.event_name}-${event.start_date}`
    return upcomingKeys.has(eventKey)
  }

  const getEventByUser = async () => {
    try {
      setGetEventStatus(true)
      const response = await axios.get(`${localserver}/get_event`, {
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (response.data.success) {
        const upcoming = response.data.upcoming || []
        setUpcomingEvents(upcoming)
        setCategories({
          upcoming: upcoming,
          past: response.data.past || [],
          zonal: response.data.zonal || [],
          mmas: response.data.mmas || [],
          fmas: response.data.fmas || [],
          slp: response.data.slp || [],
        })
        setZones(response.data.zone || [])
      } else {
        console.error("Error: Failed to fetch user data")
        showToast("error", "Failed to fetch events")
      }
    } catch (error) {
      console.error("Error fetching events:", error)
      showToast("error", "An error occurred while fetching events")
    } finally {
      setGetEventStatus(false)
    }
  }

  useEffect(() => {
    getEventByUser()
  }, [])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setSearchQuery("")
    if (tab === "zonal") {
      setSelectedZone("all")
    }
  }

  return (
    <>
      <Header />
      <div className="upcoming_calender">
        <ToastMessage />
        <div className="events-page">
          <div className="events-container">
            {getEventStatus ? (
              <div className="loading">
                <div className="loading-spinner"></div>
                <p>Loading events...</p>
              </div>
            ) : (
              <div className="events-layout">
                {/* Left Column - Events */}
                <div className="events-column">
                  <div className="tabs-section">
                    <div className="tabs">
                      <button
                        className={`tab ${activeTab === "upcoming" ? "tab-active" : ""}`}
                        onClick={() => handleTabChange("upcoming")}
                      >
                        Upcoming Events
                      </button>
                      <button
                        className={`tab ${activeTab === "past" ? "tab-active" : ""}`}
                        onClick={() => handleTabChange("past")}
                      >
                        Past Events
                      </button>
                      <button
                        className={`tab ${activeTab === "zonal" ? "tab-active" : ""}`}
                        onClick={() => handleTabChange("zonal")}
                      >
                        Zonal Events
                      </button>
                      <button
                        className={`tab ${activeTab === "mmas" ? "tab-active" : ""}`}
                        onClick={() => handleTabChange("mmas")}
                      >
                        MMAS Events
                      </button>
                      <button
                        className={`tab ${activeTab === "fmas" ? "tab-active" : ""}`}
                        onClick={() => handleTabChange("fmas")}
                      >
                        FMAS Events
                      </button>
                      <button
                        className={`tab ${activeTab === "slp" ? "tab-active" : ""}`}
                        onClick={() => handleTabChange("slp")}
                      >
                        SLCP Events
                      </button>
                    </div>
                    <div className="event_total_flex">
                      <p className="events-count">
                        {searchQuery ? (
                          <>
                            Found {eventsToShow.length} events for "{searchQuery}" in {activeTab}
                          </>
                        ) : (
                          `Showing ${eventsToShow.length} ${activeTab} events`
                        )}
                      </p>

                      <div className="tab_search_input_flex">
                        <div className="search-section">
                          <div className="search-input-wrapper">
                            <FaSearch className="search-icon" />
                            <input
                              type="text"
                              placeholder="Search events..."
                              value={searchQuery}
                              onChange={(e) => handleSearch(e.target.value)}
                              className="search-input"
                            />
                            {searchQuery && (
                              <button onClick={() => setSearchQuery("")} className="clear-search-btn">
                                <FaTimes />
                              </button>
                            )}
                          </div>
                        </div>
                        {activeTab === "zonal" && (
                          <div className="zone-filter-section">
                            <select
                              value={selectedZone}
                              onChange={(e) => setSelectedZone(e.target.value)}
                              className="zone-select"
                            >
                              <option value="all">All Zones</option>
                              {zones.map((z) => (
                                <option key={z.id} value={z.zone_name}>
                                  {z.zone_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>


                  <div className="events-list">
                    {eventsToShow.length > 0 ? (
                      eventsToShow.map((event) => {
                        const eventKey = event.id ? event.id : `${event.event_name}-${event.start_date}`
                        return (
                          <div key={eventKey} className="event-card">
                            <div className="event-content">
                              <div className="event-top-row">
                                <div className="event-date-section">
                                  <div className="date-badge">
                                    {formatDate(event.start_date).month} '
                                    {formatDate(event.start_date).day.toString().slice(-2)}
                                  </div>
                                  <div className="date-day">{formatDate(event.start_date).day}</div>
                                </div>
                                <img
                                  src={event.event_logo || "/placeholder.svg"}
                                  alt="Event logo"
                                  className="event-image mobile_top_view"
                                />
                              </div>

                              <div className="event-details">
                                <div className="event-header">
                                  <h3 className="event-title">{event.event_name}</h3>
                                  {event.event_status === "live" && (
                                    <span className="live-badge">
                                      <FaCircle className="live-icon" /> Live
                                    </span>
                                  )}
                                </div>

                                <div className="event-bottom-row">
                                  <div className="event-meta">
                                    <div className="meta-item">
                                      <FaMapMarkerAlt />
                                      {event.venue_name},{event.venue_city}
                                    </div>
                                    <div className="meta-item">
                                      <FaClock />
                                      {formatTime(event.start_time)} - {formatTime(event.end_time)}
                                    </div>
                                  </div>

                                  {upcomingKeys.has(eventKey) && (
                                    <button
                                      className="register-btn"
                                      onClick={() => handleRegister(event.registration_url)}
                                    >
                                      {"Register Now"}
                                    </button>
                                  )}
                                </div>
                              </div>
                              <img
                                src={event.event_logo || "/placeholder.svg"}
                                alt="Event logo"
                                className="event-image desk_top_view"
                              />
                            </div>
                          </div>
                        )
                      })
                    ) : (
                      <div className="no-events">
                        <p>
                          {searchQuery
                            ? `No events found for "${searchQuery}" in ${activeTab}`
                            : `No ${activeTab} events`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column - Calendar */}
                <div className="calendar-column">
                  <div className="calendar-card">
                    <div className="calendar-content">
                      <div className="calendar-header">
                        <h3 className="calendar-title">{monthYear}</h3>
                        <div className="calendar-nav">
                          <button className="nav-btn" onClick={() => navigateMonth("prev")}>
                            <FaChevronLeft />
                          </button>
                          <button className="nav-btn" onClick={() => navigateMonth("next")}>
                            <FaChevronRight />
                          </button>
                        </div>
                      </div>

                      <div className="calendar-weekdays">
                        {weekDays.map((day) => (
                          <div key={day} className="weekday">
                            {day}
                          </div>
                        ))}
                      </div>

                      <div className="calendar-grid">
                        {days.map((day, index) => (
                          <div key={index} className="calendar-cell">
                            {day && (
                              <button
                                onClick={() => handleDateClick(day.dateString)}
                                className={`calendar-day ${day.hasEvents ? "has-events" : ""} ${selectedDate === day.dateString ? "selected" : ""
                                  } ${day.dateString === todayString ? "today" : ""}`}
                                style={
                                  day.dateString === todayString
                                    ? {
                                      backgroundColor: "#007bff",
                                      color: "white",
                                      borderRadius: "50%",
                                      fontWeight: "bold",
                                    }
                                    : {}
                                }
                              >
                                {day.day}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="calendar-legend">
                        <div className="legend-item">
                          <div className="legend-color"></div>
                          <span>Has Events</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedDate && selectedEvents.length > 0 && (
                    <div className="selected-events-card">
                      <div className="selected-events-header">
                        <h4 className="selected-events-title">Events on {formatDate(selectedDate).fullDate}</h4>
                        <button className="close-btn" onClick={clearSelection}>
                          <FaTimes />
                        </button>
                      </div>
                      <div className="selected-events-content">
                        <div className="selected-events-list">
                          {selectedEvents.map((event) => {
                            const eventKey = event.id ? event.id : `${event.event_name}-${event.start_date}`
                            return (
                              <div key={`calendar-${eventKey}`} className="selected-event">
                                <div className="selected-event-content">
                                  <img
                                    src={event.event_logo || "/placeholder.svg"}
                                    alt="Event logo"
                                    className="selected-event-image"
                                  />
                                  <div className="selected-event-details">
                                    <div className="selected-event-header">
                                      <h5 className="selected-event-title">{event.event_name}</h5>
                                      {event.event_status === "live" && (
                                        <span className="selected-live-badge">
                                          <FaCircle className="live-icon" /> Live
                                        </span>
                                      )}
                                    </div>
                                    <div className="selected-event-location">
                                      <FaMapMarkerAlt />
                                      <span>
                                        {event.venue_name},{event.venue_city}
                                      </span>
                                    </div>
                                    {upcomingKeys.has(eventKey) && (
                                      <button
                                        className="selected-register-btn"
                                        onClick={() => handleRegister(event.registration_url)}
                                      >
                                        {"Register Now"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}
